import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Typography,
  Button,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import RejectionPopup from "./RejectionPopup";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles(() => ({
  closeButton: {
    // position: "absolute",
    // top: theme.spacing(1),
    // right: theme.spacing(1),
    // fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const StripTestPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bedSize, setBedSize] = useState("");
  const [comment, setComment] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  let farmerPlotId = getFarmerPlotId();
  const [stripTestMultiplicationFator, setStripTestMultiplicationFator] =
    useState("");
  const [bedSizeOption, setBedSizeOption] = useState();
  const [multiplicationFactors, setMultiplicationFactors] = useState({});
  const [openRejectionPopup, setOpenRejectionPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const maxHeight = `calc(100vh - 25vh)`;

  const roles = "FARMER";
  const isSeed = false;

  const rows = [
    { label: "FF grade (>40 mmd)", type: "textfield", key: ">40mmd" },
    { label: "Goli (<40 mmd)", type: "textfield", key: "<40mmd" },
    {
      label: t("Plantation Population (3 mtr)"),
      type: "textfield",
      key: "plant_population",
    },
    {
      label: t("Estimated yield (MT)"),
      type: "textfield",
      key: "estimated_yield",
    },
  ];

  const scrollableContainerStyle = {
    maxHeight: "300px",
    overflowY: "auto",
  };

  const [formDataSet, setFormDataSet] = useState(
    existingData?.samples || [
      {
        plant_population: "",
        estimated_yield: "",
        ">40mmd": "",
        "<40mmd": "",
      },
    ]
  );

  // New state for SEED program
  const [netArea, setNetArea] = useState("");
  const [numberOfSamples, setNumberOfSamples] = useState("");
  const [sampleSize, setSampleSize] = useState(0);
  const [seedMultiplicationFactor, setSeedMultiplicationFactor] = useState(0);
  const [grades, setGrades] = useState([{ grade: "", weight: "" }]);
  const [gradeList, setGradeList] = useState([]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [wtPerM, setWtPerM] = useState(0);
  const [freshYield, setFreshYield] = useState(0);
  const [actualYield, setActualYield] = useState(0);
  const [totalProduction, setTotalProduction] = useState(0);
  const [numberOfBags, setNumberOfBags] = useState(0);

  const initialErrors = {
    selectedDate: "",
    bedSize: "",
    formDataSet:
      formDataSet.length > 0
        ? formDataSet.map(() => ({
            ">40mmd": "",
            "<40mmd": "",
            plant_population: "",
            estimated_yield: "",
          }))
        : [
            {
              ">40mmd": "",
              "<40mmd": "",
              plant_population: "",
              estimated_yield: "",
            },
          ],
    netArea: "",
    numberOfSamples: "",
    grades: "",
  };

  const [errors, setErrors] = useState(initialErrors);

  const preFetchGrade = () => {
    instance
      .get("/seedprogram/seed-grades/")
      .then((res) => {
        if (res?.data) {
          setGradeList(res.data);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
        showAlert("error", "Error while fetching Grade");
      });
  };
  useEffect(() => {
    preFetchGrade();
  }, isSeed);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await instance(
          `plotengagement/get-strip-test-multiplication-factor`
        );
        let res_data = res.data;
        if (res_data.length > 0) {
          const factors = {};
          res_data.forEach((item) => {
            factors[item.bed_size] = item.multiplication_factor;
          });
          setMultiplicationFactors(factors);
        }
        const bed_size_res = await instance(
          `/plotengagement/get-inches?stage=BED_SIZE`
        );
        let bed_sizeres_data = bed_size_res.data;
        if (bed_sizeres_data.length > 0) {
          setBedSizeOption(bed_sizeres_data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
    fetchBedSize();
  }, []);

  useEffect(() => {
    if (farmerPlotId) {
      const fetchData = async () => {
        try {
          const response = await instance(
            `plotengagement/get-strip-test?farmer_plot=${farmerPlotId}`
          );
          let data = response.data;
          if (data.length > 0) {
            setExistingData(data[0]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    if (existingData) {
      setSelectedDate(
        existingData.activity_date ? new Date(existingData.activity_date) : null
      );
      setBedSize(existingData.bed_size || "");
      setComment(existingData.comments || "");
      if (isSeed) {
        const sample = existingData.samples[0];

        setNetArea(sample.net_area || "");
        setNumberOfSamples(sample.number_of_samples || "");
        setSampleSize(sample.sample_size || 0);
        setSeedMultiplicationFactor(sample.multiplication_factor || 0);
        setGrades(sample.grades || [{ grade: "", weight: "" }]);
        setTotalWeight(sample.total_weight || 0);
        setWtPerM(sample.wt_per_m || 0);
        setFreshYield(sample.fresh_yield || 0);
        setActualYield(sample.actual_yield || 0);
        setTotalProduction(sample.total_production || 0);
        setNumberOfBags(sample.number_of_bags || 0);
      } else {
        setFormDataSet(
          existingData.samples || [
            {
              ">40mmd": "",
              "<40mmd": "",
              plant_population: "",
              estimated_yield: "",
            },
          ]
        );
      }
    }
  }, [existingData, isSeed]);

  const fetchBedSize = async () => {
    try {
      const response = await instance.get(
        `plotengagement/get-bed-size?farmer_plot=${farmerPlotId}`
      );
      const bedSizeData = response.data;
      if (bedSizeData) {
        setBedSize(parseFloat(bedSizeData));
        try {
          const res = await instance(
            `/plotengagement/get-strip-test-multiplication-factor?bed_size=${bedSizeData}`
          );
          const res_data = res.data;
          if (res_data.length > 0) {
            const factors = { ...multiplicationFactors };
            res_data.forEach((item) => {
              factors[item.bed_size] = item.multiplication_factor;
            });
            setStripTestMultiplicationFator(factors[parseFloat(bedSizeData)]);
            setSeedMultiplicationFactor(
              4000 / (parseFloat(bedSizeData) * 0.025)
            );
          }
        } catch (error) {
          console.error("Error fetching multiplication factors:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching bed size data:", error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setErrors((prevErrors) => ({
      ...prevErrors,
      selectedDate: "",
    }));
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleBedSize = async (event) => {
    const selectedBedSize = event.target.value;
    setBedSize(selectedBedSize);
    const multiplicationFactor = multiplicationFactors[selectedBedSize];

    if (multiplicationFactor) {
      setStripTestMultiplicationFator(multiplicationFactor);

      if (isSeed) {
        const factor = 4000 / (parseFloat(selectedBedSize) * 0.025);
        setSeedMultiplicationFactor(factor);
        updateCalculations(netArea, numberOfSamples, grades, factor);
      } else {
        const existingValues = formDataSet.some(
          (formData) => formData["<40mmd"] !== "" && formData[">40mmd"] !== ""
        );

        if (existingValues) {
          setFormDataSet((prevData) => {
            const updatedData = prevData.map((formData) => {
              if (formData["<40mmd"] !== "" && formData[">40mmd"] !== "") {
                const lessThan40 = parseFloat(formData["<40mmd"]);
                const moreThan40 = parseFloat(formData[">40mmd"]);

                if (!isNaN(lessThan40) && !isNaN(moreThan40)) {
                  const estimatedYield =
                    (lessThan40 + moreThan40) * multiplicationFactor;
                  formData["estimated_yield"] = estimatedYield.toFixed(4);
                } else {
                  formData["estimated_yield"] = "0.00";
                }
              }
              return formData;
            });
            return updatedData;
          });
        }
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      bedSize: "",
    }));
  };

  const handleChange = (field, value, index) => {
    setFormDataSet((prevData) => {
      const newData = prevData.map((item, i) => {
        if (i === index) {
          let updatedItem = {
            ...item,
            [field === "chemicalName" ? "selectedChemical" : field]: value,
          };
          if (field === "<40mmd" || field === ">40mmd") {
            const lessThan40 = parseFloat(updatedItem["<40mmd"]);
            const moreThan40 = parseFloat(updatedItem[">40mmd"]);

            if (!isNaN(lessThan40) && !isNaN(moreThan40)) {
              const estimatedYield =
                (lessThan40 + moreThan40) * stripTestMultiplicationFator;
              updatedItem["estimated_yield"] = estimatedYield.toFixed(4);
            } else {
              updatedItem["estimated_yield"] = "0.00";
            }
          }

          return updatedItem;
        }
        return item;
      });
      return newData;
    });
    if (
      formDataSet[index] &&
      (field === "<40mmd" || field === ">40mmd" || field === "plant_population")
    ) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (!newErrors.formDataSet[index]) {
          newErrors.formDataSet[index] = {};
        }
        newErrors.formDataSet[index][field] = "";
        return newErrors;
      });
    }
  };

  const handleAddMore = () => {
    const newFormData = {
      plant_population: "",
      estimated_yield: "",
      ">40mmd": "",
      "<40mmd": "",
    };

    setFormDataSet((prevData) => [...prevData, newFormData]);

    setErrors((prevErrors) => ({
      ...prevErrors,
      formDataSet: [
        ...prevErrors.formDataSet,
        {
          ">40mmd": "",
          "<40mmd": "",
          plant_population: "",
          estimated_yield: "",
        },
      ],
    }));
  };

  const handleDeleteForm = (index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 1);
      return newData;
    });
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleReject = (reason) => {
    const rejectionData = {
      id: existingData.id,
      stage: "strip_test",
      status: "REJECTED",
      reason_for_rejection: reason,
    };

    instance
      .post("plotengagement/ao-status-update", rejectionData)
      .then(() => {
        setOpenRejectionPopup(false);
        showAlert("success", "Data Rejected");
        setOpen(false);
        onClose();
      })
      .catch((error) => {
        console.error("Error rejecting data:", error);
      });
  };

  const handleApprove = () => {
    const approvalData = {
      id: existingData.id,
      stage: "strip_test",
      status: "APPROVED",
    };

    instance
      .post("plotengagement/ao-status-update", approvalData)
      .then(() => {
        showAlert("success", "Data Approved");
        setOpen(false);
        onClose();
      })
      .catch((error) => {
        console.error("Error approving data:", error);
      });
  };

  const handleNetAreaChange = (event) => {
    const value = event.target.value;
    setNetArea(value);
    updateCalculations(
      value,
      numberOfSamples,
      grades,
      seedMultiplicationFactor
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      netArea: "",
    }));
  };

  const handleNumberOfSamplesChange = (event) => {
    const value = event.target.value;
    setNumberOfSamples(value);
    setSampleSize(value * 6);
    updateCalculations(netArea, value, grades, seedMultiplicationFactor);
    setErrors((prevErrors) => ({
      ...prevErrors,
      numberOfSamples: "",
    }));
  };

  const handleGradeChange = (index, field, value) => {
    const newGrades = [...grades];
    newGrades[index][field] = value;
    setGrades(newGrades);
    updateCalculations(
      netArea,
      numberOfSamples,
      newGrades,
      seedMultiplicationFactor
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      grades: "",
    }));
  };

  const addGrade = () => {
    setGrades([...grades, { grade: "", weight: "" }]);
  };

  const removeGrade = (index) => {
    const newGrades = grades.filter((_, i) => i !== index);
    setGrades(newGrades);
    updateCalculations(
      netArea,
      numberOfSamples,
      newGrades,
      seedMultiplicationFactor
    );
  };

  const updateCalculations = (area, samples, currentGrades, factor) => {
    const calculatedSampleSize = samples * 6;
    setSampleSize(calculatedSampleSize);

    const calculatedTotalWeight = currentGrades.reduce(
      (sum, grade) => sum + parseFloat(grade.weight || 0),
      0
    );
    setTotalWeight(calculatedTotalWeight);

    const calculatedWtPerM = calculatedTotalWeight / calculatedSampleSize;
    setWtPerM(calculatedWtPerM);

    const calculatedFreshYield = calculatedWtPerM * factor;
    setFreshYield(calculatedFreshYield);

    const calculatedActualYield = calculatedFreshYield * 0.85;
    setActualYield(calculatedActualYield);

    const calculatedTotalProduction =
      calculatedActualYield * parseFloat(area || 0);
    setTotalProduction(calculatedTotalProduction);

    const calculatedNumberOfBags = Math.ceil(calculatedTotalProduction / 50);
    setNumberOfBags(calculatedNumberOfBags);
  };

  const validateForm = () => {
    let formErrors = {
      selectedDate: "",
      bedSize: "",
      formDataSet: Array.from({ length: formDataSet.length }, () => ({
        ">40mmd": "",
        "<40mmd": "",
        plant_population: "",
        estimated_yield: "",
      })),
      netArea: "",
      numberOfSamples: "",
      grades: "",
    };

    let hasErrors = false;

    if (!selectedDate) {
      formErrors.selectedDate = "Date is required";
      hasErrors = true;
    }

    if (!bedSize) {
      formErrors.bedSize = "Bed Size is required";
      hasErrors = true;
    }

    if (isSeed) {
      if (!netArea) {
        formErrors.netArea = "Net Area is required";
        hasErrors = true;
      }
      if (!numberOfSamples) {
        formErrors.numberOfSamples = "Number of Samples is required";
        hasErrors = true;
      }
      if (grades.some((grade) => !grade.grade || !grade.weight)) {
        formErrors.grades = "All grades must have a name and weight";
        hasErrors = true;
      }
    } else {
      formDataSet.forEach((formData, index) => {
        if (
          !formData[">40mmd"] ||
          !formData["<40mmd"] ||
          !formData["plant_population"] ||
          !formData["estimated_yield"]
        ) {
          formErrors.formDataSet[index] = {
            ">40mmd": !formData[">40mmd"] ? "Field is required" : "",
            "<40mmd": !formData["<40mmd"] ? "Field is required" : "",
            plant_population: !formData["plant_population"]
              ? "Field is required"
              : "",
            estimated_yield: !formData["estimated_yield"]
              ? "Field is required"
              : "",
          };
          hasErrors = true;
        }
      });
    }

    setErrors(formErrors);

    return hasErrors;
  };

  const handleSubmit = () => {
    const hasErrors = validateForm();
    if (hasErrors) {
      return;
    }

    const formatDate = (date) => {
      const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
      return date
        ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
          )}`
        : null;
    };

    let formattedData;

    if (isSeed) {
      formattedData = {
        activity_date: selectedDate ? formatDate(selectedDate) : null,
        bed_size: bedSize,
        samples: formDataSet.map(() => ({
          net_area: parseFloat(netArea),
          number_of_samples: parseInt(numberOfSamples),
          sample_size: sampleSize,
          multiplication_factor: seedMultiplicationFactor,
          grades: grades,
          total_weight: totalWeight,
          wt_per_m: wtPerM,
          fresh_yield: freshYield,
          actual_yield: actualYield,
          total_production: totalProduction,
          number_of_bags: numberOfBags,
        })),
        comments: comment,
        is_seed: true,
      };
    } else {
      formattedData = {
        activity_date: selectedDate ? formatDate(selectedDate) : null,
        bed_size: bedSize,
        samples: formDataSet.map((formData) => ({
          ">40mmd": formData[">40mmd"] ? parseFloat(formData[">40mmd"]) : 0,
          "<40mmd": formData["<40mmd"] ? parseFloat(formData["<40mmd"]) : 0,
          plant_population: formData["plant_population"]
            ? parseInt(formData["plant_population"])
            : 0,
          estimated_yield: formData["estimated_yield"]
            ? parseFloat(formData["estimated_yield"])
            : 0,
        })),
        comments: comment,
        is_seed: false,
      };
    }

    if (isEditMode) {
      formattedData.id = existingData.id;
    } else {
      formattedData.farmer_plot = farmerPlotId;
    }

    if (existingData && existingData.status === "REJECTED") {
      formattedData.resubmit = true;
    }

    const apiUrl = isEditMode
      ? "plotengagement/update-strip-test"
      : "plotengagement/create-strip-test";

    instance
      .post(apiUrl, formattedData)
      .then((res) => {
        if (res?.data) {
          setShowSuccessPopup(true);
          setTimeout(() => {
            setShowSuccessPopup(false);
            onClose();
          }, 2000);
        }
      })
      .catch((err) => {
        console.log("Error", err);
        showAlert("error", "Some Error Occurred");
      });
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: maxHeight } }}
      >
        <Grid container>
          <Grid item xs={10}>
            <DialogTitle style={{ padding: "0px" }}>
              {t("Strip test")}
            </DialogTitle>
          </Grid>
          {existingData !== null ? (
            <Grid item xs={1} style={{ marginTop: "10px" }}>
              <img src={EditIcon} onClick={handleEditClick} alt="edit" />
            </Grid>
          ) : null}
          <Grid item xs={1}>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={handleClose}
              sx={{ position: "inherit" }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={7} style={{ marginTop: "20px" }}>
            <InputLabel style={{ fontSize: "12px" }}>{t("Date")}</InputLabel>
          </Grid>

          <Grid item xs={5}>
            <TextField
              placeholder="DD/MM/YYYY"
              fullWidth
              disabled={!isEditMode && existingData !== null}
              value={selectedDate ? selectedDate.toLocaleDateString() : ""}
              helperText={errors.selectedDate}
              error={!!errors.selectedDate}
              InputProps={{
                sx: {
                  height: "40px",
                  borderRadius: "5px", // You can adjust this if needed
                  marginTop: "10px",
                  background: "#FFF",
                  border: "1px solid #C7BDBD",
                },
                readOnly: true,
                onClick: () => document.getElementById("date-picker").click(),
              }}
            />
          </Grid>
          <DatePicker
            id="date-picker"
            dateFormat="dd/MM/yyyy"
            selected={selectedDate}
            onChange={handleDateChange}
            className={classes.datepicker}
          />
          {bedSizeOption && bedSizeOption.length > 0 ? (
            <Grid container sx={{ marginTop: "13px" }}>
              <Grid item xs={6}>
                <Typography style={{ fontSize: "13px" }}>
                  {t("Bed Size")}{" "}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Select
                  value={bedSize}
                  disabled={!isEditMode && existingData !== null}
                  onChange={handleBedSize}
                  helperText={errors.bedSize}
                  error={!!errors.bedSize}
                  style={{ width: "100%" }}
                  sx={{
                    height: "40px",
                    border: "1px solid #9C9FA0",
                    borderRadius: "5px",
                  }}
                >
                  {bedSizeOption.map((option) => (
                    <MenuItem key={option.id} value={option.inch}>
                      {option.inch}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
        {isSeed ? (
          <div style={{ padding: "16px" }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <InputLabel>Net Area</InputLabel>
                <TextField
                  type="number"
                  value={netArea}
                  onChange={handleNetAreaChange}
                  fullWidth
                  disabled={!isEditMode && existingData !== null}
                  error={!!errors.netArea}
                  helperText={errors.netArea}
                />
              </Grid>
              <Grid item xs={6}>
                <InputLabel>Number of Samples</InputLabel>
                <TextField
                  type="number"
                  value={numberOfSamples}
                  onChange={handleNumberOfSamplesChange}
                  fullWidth
                  disabled={!isEditMode && existingData !== null}
                  error={!!errors.numberOfSamples}
                  helperText={errors.numberOfSamples}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: "16px" }}>
              <Grid item xs={6}>
                <TextField
                  label="Sample Size"
                  value={sampleSize}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Multiplication Factor"
                  value={seedMultiplicationFactor.toFixed(2)}
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>

            {/* Grades Section */}
            <Grid
              container
              spacing={2}
              style={{ marginTop: "16px", marginLeft: "0px" }}
            >
              {grades.map((grade, index) => (
                <Grid
                  container
                  spacing={2}
                  key={index}
                  style={{ marginTop: "8px" }}
                >
                  <Grid item xs={4}>
                    <FormControl
                      fullWidth
                      disabled={!isEditMode && existingData !== null}
                    >
                      <InputLabel>Grade</InputLabel>
                      <Select
                        label="Grade"
                        value={grade.grade || ""}
                        onChange={(e) =>
                          handleGradeChange(index, "grade", e.target.value)
                        }
                      >
                        {gradeList.map((g) => (
                          <MenuItem key={g.code} value={g.code}>
                            {g.code}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={4}>
                    <TextField
                      label="Weight"
                      type="number"
                      value={grade.weight}
                      onChange={(e) =>
                        handleGradeChange(index, "weight", e.target.value)
                      }
                      fullWidth
                      disabled={!isEditMode && existingData !== null}
                    />
                  </Grid>

                  {/* Remove grade button */}
                  <Grid item xs={4}>
                    {(isEditMode || !existingData) && (
                      <Button onClick={() => removeGrade(index)}>Remove</Button>
                    )}
                  </Grid>
                </Grid>
              ))}

              {/* Add grade button */}
              {(isEditMode || !existingData) && (
                <Button onClick={addGrade}>Add Grade</Button>
              )}

              {/* Error display */}
              {errors.grades && (
                <Typography color="error">{errors.grades}</Typography>
              )}
            </Grid>
            {/* New Grid container for the fields to display beside each other */}

            <Grid container spacing={2} style={{ marginTop: "16px" }}>
              <Grid item xs={6}>
                <TextField
                  label="Total Weight"
                  value={totalWeight.toFixed(2)}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Wt./M"
                  value={wtPerM.toFixed(2)}
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: "16px" }}>
              <Grid item xs={6}>
                <TextField
                  label="Fresh Yield"
                  value={freshYield.toFixed(2)}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Actual Yield"
                  value={actualYield.toFixed(2)}
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: "16px" }}>
              <Grid item xs={6}>
                <TextField
                  label="Total Production"
                  value={totalProduction.toFixed(2)}
                  fullWidth
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Number of Bags"
                  value={numberOfBags}
                  fullWidth
                  disabled
                />
              </Grid>
            </Grid>
          </div>
        ) : (
          <div style={{ padding: "16px" }}>
            <div>
              <Typography className={classes.taxtTitle}>
                {t("Enter sample weight in kg")}
              </Typography>
            </div>
            <div style={{ ...scrollableContainerStyle }}>
              {formDataSet.map((formData, formIndex) => (
                <div key={formIndex} style={{ padding: "0px" }}>
                  {rows.map((row, index) => (
                    <Grid key={index} container style={{ marginTop: "10px" }}>
                      <Grid item xs={6} style={{ marginTop: "10px" }}>
                        <InputLabel className={classes.taxtLabel}>
                          {row.label === "Plantation Population (3 mtr)" ? (
                            <>
                              {t("Plantation Population")}
                              <br />
                              {t("(3 mtr)")}
                            </>
                          ) : (
                            t(row.label)
                          )}
                        </InputLabel>
                      </Grid>
                      <Grid item xs={6}>
                        {row.type === "dropdown" ? (
                          <Select
                            value={formData[row.key]}
                            disabled={!isEditMode && existingData !== null}
                            onChange={(event) =>
                              handleChange(
                                row.key,
                                event.target.value,
                                formIndex
                              )
                            }
                            helperText={errors.formDataSet[formIndex][row.key]}
                            error={!!errors.formDataSet[formIndex][row.key]}
                            style={{ width: "100%" }}
                            sx={{
                              height: "40px",
                              border: "1px solid #9C9FA0",
                              borderRadius: "5px",
                            }}
                          >
                            {row.options.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : row.key === "estimated_yield" ? (
                          <TextField
                            type="text"
                            value={
                              formData[row.key] ? `${formData[row.key]}` : ""
                            }
                            disabled
                            style={{ width: "100%" }}
                            size="small"
                            InputProps={{
                              sx: {
                                height: "40px",
                                borderRadius: "5px",
                                marginTop: "10px",
                                background: "#FFF",
                                border: "1px solid #C7BDBD",
                                textAlign: "right",
                                "&::placeholder": {
                                  textAlign: "right",
                                },
                              },
                              endAdornment: t("MT"),
                            }}
                          />
                        ) : (
                          <TextField
                            type="number"
                            helperText={
                              errors.formDataSet &&
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex][row.key]
                            }
                            error={
                              !!(
                                errors.formDataSet &&
                                errors.formDataSet[formIndex] &&
                                errors.formDataSet[formIndex][row.key]
                              )
                            }
                            disabled={!isEditMode && existingData !== null}
                            value={formData[row.key]}
                            onChange={(event) =>
                              handleChange(
                                row.key,
                                event.target.value,
                                formIndex
                              )
                            }
                            style={{ width: "100%" }}
                            size="small"
                            InputProps={{
                              sx: {
                                height: "40px",
                                borderRadius: "5px",
                                marginTop: "10px",
                                background: "#FFF",
                                border: "1px solid #C7BDBD",
                              },
                              endAdornment:
                                row.key === ">40mmd" || row.key === "<40mmd"
                                  ? t("kg")
                                  : null,
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  ))}

                  {formIndex < formDataSet.length - 1 && (
                    <div>
                      <Divider style={{ margin: "10px 0" }} />
                      {isEditMode && existingData !== null ? (
                        <Grid container>
                          <Grid item xs={10}></Grid>
                          <Grid item xs={2}>
                            <IconButton
                              aria-label="delete"
                              onClick={() =>
                                handleDeleteForm(
                                  !isEditMode ? formIndex + 1 : formIndex + 1
                                )
                              }
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ) : existingData === null ? (
                        <Grid container>
                          <Grid item xs={10}></Grid>
                          <Grid item xs={2}>
                            <IconButton
                              aria-label="delete"
                              onClick={() =>
                                handleDeleteForm(
                                  !isEditMode ? formIndex + 1 : formIndex + 1
                                )
                              }
                            >
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <DialogActions style={{ display: "grid" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                  marginRight: "16px",
                  width: "140px",
                }}
              >
                <Button
                  onClick={handleAddMore}
                  style={{
                    background:
                      !isEditMode && existingData !== null
                        ? "rgba(111, 163, 78, 0.5)"
                        : "#6FA34E",
                    border: "#538434",
                    borderRadius: "10px",
                    color: "#FFF",
                    textTransform: "none",
                  }}
                  disabled={!isEditMode && existingData !== null}
                >
                  <AddIcon style={{ marginRight: "5px", fontSize: "15px" }} />{" "}
                  {t("Add More")}
                </Button>
              </div>
            </DialogActions>
          </div>
        )}

        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment (Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
            disabled={!isEditMode && existingData !== null}
          />
          {existingData &&
            existingData.status === "REJECTED" &&
            existingData.reason_for_rejection &&
            !isEditMode &&
            roles === "FARMER" && (
              <Typography style={{ marginTop: "10px", color: "red" }}>
                {t("Reason for Rejection")}: {existingData.reason_for_rejection}
              </Typography>
            )}
        </div>

        <div
          style={{ display: "flex", margin: "auto", justifyContent: "center" }}
        >
          {existingData && isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Update")}
              </CustomButton>
            </div>
          ) : !existingData && !isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Submit")}
              </CustomButton>
            </div>
          ) : null}
        </div>
        {existingData &&
        existingData.status === "PENDING" &&
        !isEditMode &&
        (roles === "AO" || roles === "SPVR") ? (
          <div
            style={{
              display: "flex",
              margin: "auto",
              justifyContent: "center",
            }}
          >
            <CustomButton onClick={() => setOpenRejectionPopup(true)} hideIcon>
              {t("Reject")}
            </CustomButton>
            <div style={{ marginLeft: "10px" }}></div>
            <CustomButton onClick={handleApprove} hideIcon>
              {t("Approve")}
            </CustomButton>
          </div>
        ) : null}
        <RejectionPopup
          open={openRejectionPopup}
          handleClose={() => setOpenRejectionPopup(false)}
          handleSubmit={handleReject}
        />
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default StripTestPopup;
